import { Suspense, useEffect, useState } from 'react';
import { Route, Routes, useLocation, useSearchParams } from 'react-router-dom';
import { AppShell } from './layouts/app-shell';
import { LoadingBlock } from './shared/components/loading-block';
import { PageErrorBoundary } from './shared/components/page-error-boundary';
import { RequireAuth } from './shared/components/require-auth';
import { RequireRoles } from './shared/components/require-roles';
import { INSTALLER_READ_ROLES } from './features/installers/roles';
import { PRODUCTION_DASHBOARD_ROLES, PROJECTS_DASHBOARD_ROLES } from './shared/dashboard-roles';
import { environment } from '../environments/environment';
import { lazyPage } from './shared/lazy-page';
import {
  MICROSOFT_LINKED_PARAM,
  MICROSOFT_LINKED_VALUE,
  useMicrosoftLinking,
} from './shared/hooks/use-microsoft-linking';

// Every route is loaded on demand. The remote is one bundle for the whole of
// the migrated product, so anything imported eagerly here — a PDF renderer, an
// editor, a chart library — is paid for by every screen that never uses it.
const ProjectKanbanPage = lazyPage(() =>
  import('./projects/kanban/project-kanban-page').then((module) => ({
    default: module.ProjectKanbanPage,
  })),
);
const ProjectViewPage = lazyPage(() =>
  import('./features/projects/pages/project-view-page').then((module) => ({
    default: module.ProjectViewPage,
  })),
);
const LatestProjectPage = lazyPage(() =>
  import('./features/projects/pages/latest-project-page').then((module) => ({
    default: module.LatestProjectPage,
  })),
);
const MyTasksPage = lazyPage(() =>
  import('./projects/my-tasks/my-tasks-page').then((module) => ({ default: module.MyTasksPage })),
);
const QuotesPage = lazyPage(() =>
  import('./features/quotes/pages/quotes-page').then((module) => ({
    default: module.QuotesPage,
  })),
);
// Its own chunk for a reason: the estimator pulls in pixi through the 2D
// renderer, and three.js on the first press of 3D. Neither should reach a
// reader who never opens a quote.
const EstimatorPage = lazyPage(() =>
  import('./features/estimator/pages/estimator-page').then((module) => ({
    default: module.EstimatorPage,
  })),
);
const QuoteDocumentPage = lazyPage(() =>
  import('./quoting/quote-document/quote-document-page').then((module) => ({
    default: module.QuoteDocumentPage,
  })),
);
const QuoteFollowUpsPage = lazyPage(() =>
  import('./quoting/quote-follow-ups/quote-follow-ups-page').then((module) => ({
    default: module.QuoteFollowUpsPage,
  })),
);
const QuoteSendQueuePage = lazyPage(() =>
  import('./quoting/quote-send-queue/quote-send-queue-page').then((module) => ({
    default: module.QuoteSendQueuePage,
  })),
);
const QuoteConversionPage = lazyPage(() =>
  import('./quoting/quote-conversion/quote-conversion-page').then((module) => ({
    default: module.QuoteConversionPage,
  })),
);
const QuoteTemplateAdminPage = lazyPage(() =>
  import('./data-management/quote-templates/quote-template-admin-page').then((module) => ({
    default: module.QuoteTemplateAdminPage,
  })),
);
const EmailTemplateAdminPage = lazyPage(() =>
  import('./data-management/email-templates/email-template-admin-page').then((module) => ({
    default: module.EmailTemplateAdminPage,
  })),
);
const PortalCollateralAdminPage = lazyPage(() =>
  import('./data-management/portal-collateral/portal-collateral-admin-page').then((module) => ({
    default: module.PortalCollateralAdminPage,
  })),
);
const LetterheadAdminPage = lazyPage(() =>
  import('./data-management/letterhead/letterhead-admin-page').then((module) => ({
    default: module.LetterheadAdminPage,
  })),
);
const AuditLogPage = lazyPage(() =>
  import('./features/audit-log/pages/audit-log-page').then((module) => ({
    default: module.AuditLogPage,
  })),
);
const OnlineUsersPage = lazyPage(() =>
  import('./features/online-users/pages/online-users-page').then((module) => ({
    default: module.OnlineUsersPage,
  })),
);
const CataloguePage = lazyPage(() =>
  import('./data-management/catalogue/catalogue-page').then((module) => ({
    default: module.CataloguePage,
  })),
);
const ScheduledPricesPage = lazyPage(() =>
  import('./data-management/scheduled-prices/scheduled-prices-page').then((module) => ({
    default: module.ScheduledPricesPage,
  })),
);
const DetailersDiaryPage = lazyPage(() =>
  import('./features/detailers-diary/pages/detailers-diary-page').then((module) => ({
    default: module.DetailersDiaryPage,
  })),
);
const DetailingPage = lazyPage(() =>
  import('./features/detailing/pages/detailing-page').then((module) => ({
    default: module.DetailingPage,
  })),
);
const PlannerPage = lazyPage(() =>
  import('./features/planner/pages/planner-page').then((module) => ({
    default: module.PlannerPage,
  })),
);
const DistributorsPage = lazyPage(() =>
  import('./features/distributors/pages/distributors-page').then((module) => ({
    default: module.DistributorsPage,
  })),
);
const SalesReportsPage = lazyPage(() =>
  import('./features/sales-reports/pages/sales-reports-page').then((module) => ({
    default: module.SalesReportsPage,
  })),
);
const EnquiriesBoardPage = lazyPage(() =>
  import('./features/enquiries/pages/enquiries-board-page').then((module) => ({
    default: module.EnquiriesBoardPage,
  })),
);
const EnquiriesListPage = lazyPage(() =>
  import('./features/enquiries/pages/enquiries-list-page').then((module) => ({
    default: module.EnquiriesListPage,
  })),
);
const EnquiriesReportsPage = lazyPage(() =>
  import('./features/enquiries-reports/pages/enquiries-reports-page').then((module) => ({
    default: module.EnquiriesReportsPage,
  })),
);
const PeopleAndAccessPage = lazyPage(() =>
  import('./user-management/people-and-access-page').then((module) => ({
    default: module.PeopleAndAccessPage,
  })),
);
const OrderAcknowledgementsSearchPage = lazyPage(() =>
  import('./features/order-acknowledgements/pages/order-acknowledgements-search-page').then(
    (module) => ({ default: module.OrderAcknowledgementsSearchPage }),
  ),
);
const OrderAcknowledgementsPage = lazyPage(() =>
  import('./features/order-acknowledgements/pages/order-acknowledgements-page').then((module) => ({
    default: module.OrderAcknowledgementsPage,
  })),
);
const PurchaseOrdersPage = lazyPage(() =>
  import('./features/purchase-orders/pages/purchase-orders-page').then((module) => ({
    default: module.PurchaseOrdersPage,
  })),
);
const AccountingReportsPage = lazyPage(() =>
  import('./features/accounting-reports/pages/accounting-reports-page').then((module) => ({
    default: module.AccountingReportsPage,
  })),
);
const AdminDashboardPage = lazyPage(() =>
  import('./features/admin-dashboard/pages/admin-dashboard-page').then((module) => ({
    default: module.AdminDashboardPage,
  })),
);
const AccountingDashboardPage = lazyPage(() =>
  import('./features/accounting-dashboard/pages/accounting-dashboard-page').then((module) => ({
    default: module.AccountingDashboardPage,
  })),
);
const DirectorsDashboardPage = lazyPage(() =>
  import('./features/directors-dashboard/pages/directors-dashboard-page').then((module) => ({
    default: module.DirectorsDashboardPage,
  })),
);
const EmailLogsPage = lazyPage(() =>
  import('./features/email-logs/pages/email-logs-page').then((module) => ({
    default: module.EmailLogsPage,
  })),
);
const InstallersPage = lazyPage(() =>
  import('./features/installers/pages/installers-page').then((module) => ({
    default: module.InstallersPage,
  })),
);
const StockControlPage = lazyPage(() =>
  import('./features/stock-control/pages/stock-control-page').then((module) => ({
    default: module.StockControlPage,
  })),
);
const BudgetQuotesPage = lazyPage(() =>
  import('./features/budget-quotes/pages/budget-quotes-page').then((module) => ({
    default: module.BudgetQuotesPage,
  })),
);
const SalesDashboardPage = lazyPage(() =>
  import('./features/sales-dashboard/pages/sales-dashboard-page').then((module) => ({
    default: module.SalesDashboardPage,
  })),
);
const QuotingDashboardPage = lazyPage(() =>
  import('./features/quoting-dashboard/pages/quoting-dashboard-page').then((module) => ({
    default: module.QuotingDashboardPage,
  })),
);
const ProjectsDashboardPage = lazyPage(() =>
  import('./features/projects-dashboard/pages/projects-dashboard-page').then((module) => ({
    default: module.ProjectsDashboardPage,
  })),
);
const DrawingBookingsPage = lazyPage(() =>
  import('./features/drawing-bookings/pages/drawing-bookings-page').then((module) => ({
    default: module.DrawingBookingsPage,
  })),
);
const IntegrationsPage = lazyPage(() =>
  import('./features/integrations/pages/integrations-page').then((module) => ({
    default: module.IntegrationsPage,
  })),
);
const GpCalculatorPage = lazyPage(() =>
  import('./features/gp-calculator/pages/gp-calculator-page').then((module) => ({
    default: module.GpCalculatorPage,
  })),
);
const ProductionDashboardPage = lazyPage(() =>
  import('./features/production-dashboard/pages/production-dashboard-page').then((module) => ({
    default: module.ProductionDashboardPage,
  })),
);
const ProductionPlannerPage = lazyPage(() =>
  import('./features/production-planner/pages/production-planner-page').then((module) => ({
    default: module.ProductionPlannerPage,
  })),
);
const SuppliersPage = lazyPage(() =>
  import('./features/suppliers/pages/suppliers-page').then((module) => ({
    default: module.SuppliersPage,
  })),
);
const DateChangesPage = lazyPage(() =>
  import('./features/date-changes/pages/date-changes-page').then((module) => ({
    default: module.DateChangesPage,
  })),
);
const OrdersPage = lazyPage(() =>
  import('./features/orders/pages/orders-page').then((module) => ({
    default: module.OrdersPage,
  })),
);
const ContractsReportsPage = lazyPage(() =>
  import('./features/contracts-reports/pages/contracts-reports-page').then((module) => ({
    default: module.ContractsReportsPage,
  })),
);
const ProductionReportsPage = lazyPage(() =>
  import('./features/production-reports/pages/production-reports-page').then((module) => ({
    default: module.ProductionReportsPage,
  })),
);
const ProjectSavingsPage = lazyPage(() =>
  import('./features/project-savings/pages/project-savings-page').then((module) => ({
    default: module.ProjectSavingsPage,
  })),
);
// The account screens are the same screen in the distributor portal, so they
// live in `@iqd/shared-app` and both products render the one implementation.
// These two need the app's environment, and `lazyPage` only takes prop-less
// pages — so the props are supplied here, inside the lazy import. A static
// import of the library would also break Nx's rule against mixing static and
// lazy imports of one library, and would undo the route splitting.
const CompanySettingsPage = lazyPage(() =>
  import('@iqd/shared-screens').then((module) => ({
    default: () => <module.CompanySettingsPage apiUrl={environment.apiUrl} />,
  })),
);
const UserSettingsPage = lazyPage(() =>
  import('@iqd/shared-screens').then((module) => ({
    // Named rather than the inline arrow the other wrappers use, because this
    // one holds hooks: the shared screen knows nothing about Auth0, so the
    // linking and the reading of its result both belong on this side.
    default: function UserSettings() {
      const microsoft = useMicrosoftLinking();
      const [searchParams, setSearchParams] = useSearchParams();
      // Read once and then taken out of the address, so the message does not
      // come back when the page is reloaded or the link is shared.
      const [justLinked] = useState(
        () => searchParams.get(MICROSOFT_LINKED_PARAM) === MICROSOFT_LINKED_VALUE,
      );

      useEffect(() => {
        if (!justLinked) return;
        setSearchParams({}, { replace: true });
      }, [justLinked, setSearchParams]);

      return (
        <module.UserSettingsPage
          auth0Domain={environment.auth0.domain}
          auth0ClientId={environment.auth0.clientId}
          // The claim behind `linked` comes from the token this page was
          // loaded with, which predates the link that just happened — so a
          // fresh link is reported by `justLinked` until the next sign-in
          // refreshes it.
          microsoft={
            microsoft && { ...microsoft, linked: microsoft.linked || justLinked, justLinked }
          }
        />
      );
    },
  })),
);
const SupportPage = lazyPage(() =>
  import('@iqd/shared-screens').then((module) => ({ default: module.SupportPage })),
);
const SupportCompletePage = lazyPage(() =>
  import('@iqd/shared-screens').then((module) => ({ default: module.SupportCompletePage })),
);
const NotificationsPage = lazyPage(() =>
  import('./features/notifications/pages/notifications-page').then((module) => ({
    default: module.NotificationsPage,
  })),
);
const DownloadsPage = lazyPage(() =>
  import('./features/downloads/pages/downloads-page').then((module) => ({
    default: module.DownloadsPage,
  })),
);
const DashboardPage = lazyPage(() =>
  import('./features/dashboard/pages/dashboard-page').then((module) => ({
    default: module.DashboardPage,
  })),
);
// Its own chunk on purpose: it is the one page that needs
// `@auth0/auth0-react`, which the Angular host has no provider for.
const AuthCallbackPage = lazyPage(() =>
  import('./shared/components/auth-callback').then((module) => ({
    default: module.AuthCallbackPage,
  })),
);
const TermsOfUsePage = lazyPage(() =>
  import('@iqd/shared-screens').then((module) => ({ default: module.TermsOfUsePage })),
);
// Its own chunk carries the whole of CHANGELOG.md as text, which is why this
// page is loaded on demand like the rest and imported nowhere else.
const ChangelogPage = lazyPage(() =>
  import('./features/changelog/pages/changelog-page').then((module) => ({
    default: module.ChangelogPage,
  })),
);
const UnauthorizedPage = lazyPage(() =>
  import('./features/misc/pages').then((module) => ({ default: module.UnauthorizedPage })),
);
const NotFoundPage = lazyPage(() =>
  import('./features/misc/pages').then((module) => ({ default: module.NotFoundPage })),
);

/**
 * The chunk behind each menu destination.
 *
 * Every screen is its own chunk, so the first thing a menu click does is wait
 * for a download. Pointing at the item is a second or so of warning, and that
 * is usually enough for the chunk to be there by the time the click lands.
 *
 * Keyed by the same paths `menu.ts` links to and holding the same components
 * the route table renders, so a page that moves breaks the build rather than
 * quietly warming nothing. `menu.spec` checks the two lists still line up.
 */
export const MENU_ROUTE_PAGES: Record<string, { preload: () => void }> = {
  '/accounting/dashboard': AccountingDashboardPage,
  '/directors/dashboard': DirectorsDashboardPage,
  '/admin/dashboard': AdminDashboardPage,
  '/accounting/order-acknowledgements': OrderAcknowledgementsPage,
  '/accounting/order-acknowledgements/search': OrderAcknowledgementsSearchPage,
  '/accounting/purchase-orders': PurchaseOrdersPage,
  '/accounting/reports': AccountingReportsPage,
  '/contracts/drawings': DrawingBookingsPage,
  '/contracts/installers': InstallersPage,
  '/contracts/planner': PlannerPage,
  '/contracts/reports': ContractsReportsPage,
  '/data-management': CataloguePage,
  '/data-management/audit-log': AuditLogPage,
  '/data-management/online-users': OnlineUsersPage,
  '/data-management/email-logs': EmailLogsPage,
  '/data-management/email-templates': EmailTemplateAdminPage,
  '/data-management/letterhead': LetterheadAdminPage,
  '/data-management/portal-collateral': PortalCollateralAdminPage,
  '/data-management/quote-templates': QuoteTemplateAdminPage,
  '/data-management/scheduled-prices': ScheduledPricesPage,
  '/integrations': IntegrationsPage,
  '/production/dashboard': ProductionDashboardPage,
  '/production/detailers-diary': DetailersDiaryPage,
  '/production/detailing': DetailingPage,
  '/production/orders': OrdersPage,
  '/production/planner': ProductionPlannerPage,
  '/production/report': DateChangesPage,
  '/production/reports': ProductionReportsPage,
  '/production/stock-control': StockControlPage,
  '/production/suppliers': SuppliersPage,
  '/projects': LatestProjectPage,
  '/projects/board': ProjectKanbanPage,
  '/projects/dashboard': ProjectsDashboardPage,
  '/projects/my-tasks': MyTasksPage,
  '/quoting/dashboard': QuotingDashboardPage,
  '/quoting/quote-conversion': QuoteConversionPage,
  '/quoting/quote-follow-ups': QuoteFollowUpsPage,
  '/quoting/quote-send-queue': QuoteSendQueuePage,
  '/quoting/quotes': QuotesPage,
  '/reports/project-savings': ProjectSavingsPage,
  '/sales/budget-quotes': BudgetQuotesPage,
  '/sales/dashboard': SalesDashboardPage,
  '/sales/distributors': DistributorsPage,
  '/sales/enquiries/board': EnquiriesBoardPage,
  '/sales/enquiries/reports': EnquiriesReportsPage,
  '/sales/enquiries/view': EnquiriesListPage,
  '/sales/reports': SalesReportsPage,
  '/user-management': PeopleAndAccessPage,
  '/utils/gp-calculator': GpCalculatorPage,
};

/** Starts the chunk for a menu destination, if that path has one. */
export function prefetchMenuRoute(path: string): void {
  MENU_ROUTE_PAGES[path]?.preload();
}

export function App() {
  const location = useLocation();

  return (
    <AppShell onPrefetchRoute={prefetchMenuRoute}>
      {/* Given the path rather than keyed on it: the boundary clears itself
          when the path changes, without remounting a page that never failed. */}
      <PageErrorBoundary path={location.pathname}>
        <Suspense fallback={<LoadingBlock label="Loading page" />}>
          <Routes>
            {/* The Angular host redirects '' to '/dashboard'; '/' is matched too
              so the remote has somewhere sensible to land when a redirect
              inside it (RequireRoles, for one) sends an unauthenticated user
              to the root. */}
            {['/', '/dashboard'].map((path) => (
              <Route key={path} path={path} element={<DashboardPage />} />
            ))}
            <Route
              path="/projects/dashboard"
              element={
                <RequireRoles roles={PROJECTS_DASHBOARD_ROLES}>
                  <ProjectsDashboardPage />
                </RequireRoles>
              }
            />
            <Route
              path="/projects/board"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <ProjectKanbanPage />
                </RequireRoles>
              }
            />
            <Route
              path="/projects/my-tasks"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <MyTasksPage />
                </RequireRoles>
              }
            />
            {/* The tab is a path segment; `/view` on its own is the Main
              tab, which is what every link into a project from elsewhere
              carries. */}
            <Route
              path="/projects/:projectId/view/:tab?"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <ProjectViewPage />
                </RequireRoles>
              }
            />
            <Route
              path="/projects"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <LatestProjectPage />
                </RequireRoles>
              }
            />
            {/* `view` is the path the Angular grid was on, and people have it
          bookmarked and linked from elsewhere; `/sales/enquiries` lands on the
          same list rather than nothing. */}
            {['/sales/enquiries', '/sales/enquiries/view'].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <RequireRoles roles={['internal-user', 'auditor']} match="any">
                    <EnquiriesListPage />
                  </RequireRoles>
                }
              />
            ))}
            <Route
              path="/sales/enquiries/board"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <EnquiriesBoardPage />
                </RequireRoles>
              }
            />
            <Route
              path="/sales/enquiries/reports/:report?"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <EnquiriesReportsPage />
                </RequireRoles>
              }
            />
            {/* The distributor id is part of the URL so a link to one distributor
          keeps working — the Angular screen did the same and people bookmark
          them. */}
            {['/sales/distributors', '/sales/distributors/:distributorId'].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <RequireRoles roles={['internal-user', 'auditor']} match="any">
                    <DistributorsPage />
                  </RequireRoles>
                }
              />
            ))}
            <Route
              path="/sales/dashboard"
              element={
                <RequireRoles roles={['admin']}>
                  <SalesDashboardPage />
                </RequireRoles>
              }
            />
            <Route
              path="/sales/reports/:report?"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <SalesReportsPage />
                </RequireRoles>
              }
            />
            <Route
              path="/sales/budget-quotes"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <BudgetQuotesPage />
                </RequireRoles>
              }
            />
            <Route
              path="/quoting/dashboard"
              element={
                <RequireRoles roles={['admin']}>
                  <QuotingDashboardPage />
                </RequireRoles>
              }
            />
            {/* The quote and its revision are path segments. Two patterns
              rather than one with optional parameters, because the revision
              only means anything under a quote. */}
            {[
              '/quoting/quotes',
              '/quoting/quotes/:quoteId',
              '/quoting/quotes/:quoteId/revision/:revisionId',
            ].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <RequireRoles roles={['internal-user', 'auditor']} match="any">
                    <QuotesPage />
                  </RequireRoles>
                }
              />
            ))}
            {/* The estimator is the last step of the quotes drill-down, and
              sits under it: list, quote, revision, then the option being
              priced, then whichever tab of it is open. */}
            <Route
              path="/quoting/quotes/:quoteId/revision/:revisionId/option/:optionId/:tab?"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <EstimatorPage />
                </RequireRoles>
              }
            />
            {/* Addressed by the revision alone: a document is a revision's,
              and there is one per revision. */}
            <Route
              path="/quoting/quote-document/revision/:revisionId"
              element={
                // sales-admin so whoever sends a quotation can read the one the
                // send queue is asking them to check.
                <RequireRoles
                  roles={['internal-user', 'auditor', 'admin', 'sales-admin']}
                  match="any"
                >
                  <QuoteDocumentPage />
                </RequireRoles>
              }
            />
            <Route
              path="/quoting/quote-send-queue"
              element={
                <RequireRoles roles={['admin', 'sales-admin']} match="any">
                  <QuoteSendQueuePage />
                </RequireRoles>
              }
            />
            <Route
              path="/quoting/quote-follow-ups"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <QuoteFollowUpsPage />
                </RequireRoles>
              }
            />
            <Route
              path="/quoting/quote-conversion"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <QuoteConversionPage />
                </RequireRoles>
              }
            />
            <Route
              path="/data-management/quote-templates"
              element={
                <RequireRoles roles={['admin']}>
                  <QuoteTemplateAdminPage />
                </RequireRoles>
              }
            />
            <Route
              path="/data-management/email-templates"
              element={
                <RequireRoles roles={['admin']}>
                  <EmailTemplateAdminPage />
                </RequireRoles>
              }
            />
            {/* The catalogue is data management now. /catalogue stays as an alias so
          links and bookmarks made while both screens existed still work. */}
            {['/data-management', '/data-management/catalogue'].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <RequireRoles roles={['internal-user', 'auditor']}>
                    <CataloguePage />
                  </RequireRoles>
                }
              />
            ))}
            <Route
              path="/data-management/scheduled-prices"
              element={
                <RequireRoles roles={['internal-user', 'auditor']}>
                  <ScheduledPricesPage />
                </RequireRoles>
              }
            />
            <Route
              path="/data-management/audit-log"
              element={
                <RequireRoles roles={['admin', 'auditor']}>
                  <AuditLogPage />
                </RequireRoles>
              }
            />
            <Route
              path="/admin/dashboard"
              element={
                <RequireRoles roles={['admin']}>
                  <AdminDashboardPage />
                </RequireRoles>
              }
            />
            <Route
              path="/directors/dashboard"
              element={
                <RequireRoles roles={['admin']}>
                  <DirectorsDashboardPage />
                </RequireRoles>
              }
            />
            <Route
              path="/data-management/online-users/:tab?"
              element={
                <RequireRoles roles={['admin', 'auditor']}>
                  <OnlineUsersPage />
                </RequireRoles>
              }
            />
            <Route
              path="/data-management/email-logs"
              element={
                <RequireRoles roles={['admin', 'auditor']}>
                  <EmailLogsPage />
                </RequireRoles>
              }
            />
            <Route
              path="/data-management/portal-collateral"
              element={
                <RequireRoles roles={['admin']}>
                  <PortalCollateralAdminPage />
                </RequireRoles>
              }
            />
            <Route
              path="/data-management/letterhead"
              element={
                <RequireRoles roles={['admin']}>
                  <LetterheadAdminPage />
                </RequireRoles>
              }
            />
            <Route
              path="/user-management"
              element={
                <RequireRoles roles={['admin', 'sales-admin']}>
                  <PeopleAndAccessPage />
                </RequireRoles>
              }
            />
            <Route
              path="/integrations"
              element={
                <RequireRoles roles={['admin', 'sales-admin']}>
                  <IntegrationsPage />
                </RequireRoles>
              }
            />
            <Route
              path="/production/detailing"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <DetailingPage />
                </RequireRoles>
              }
            />
            <Route
              path="/production/detailers-diary"
              element={
                <RequireRoles
                  roles={['internal-user', 'auditor', 'admin', 'production-admin']}
                  match="any"
                >
                  <DetailersDiaryPage />
                </RequireRoles>
              }
            />
            <Route
              path="/accounting/dashboard"
              element={
                <RequireRoles roles={['admin']}>
                  <AccountingDashboardPage />
                </RequireRoles>
              }
            />
            <Route
              path="/accounting/order-acknowledgements/search"
              element={
                <RequireRoles roles={['accounts-manager', 'admin']} match="any">
                  <OrderAcknowledgementsSearchPage />
                </RequireRoles>
              }
            />
            <Route
              path="/accounting/order-acknowledgements/:acknowledgementId?"
              element={
                <RequireRoles roles={['accounts-manager', 'admin']} match="any">
                  <OrderAcknowledgementsPage />
                </RequireRoles>
              }
            />
            <Route
              path="/accounting/purchase-orders/:orderId?"
              element={
                <RequireRoles
                  roles={['accounts-manager', 'admin', 'administrative-assistant']}
                  match="any"
                >
                  <PurchaseOrdersPage />
                </RequireRoles>
              }
            />
            <Route
              path="/accounting/reports/:report?"
              element={
                <RequireRoles
                  roles={['accounts-manager', 'admin', 'administrative-assistant']}
                  match="any"
                >
                  <AccountingReportsPage />
                </RequireRoles>
              }
            />
            {/* The installer id is part of the URL so a link to one installer keeps
          working — the Angular screen did the same and people bookmark them. */}
            {['/contracts/installers', '/contracts/installers/:installerId'].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <RequireRoles roles={INSTALLER_READ_ROLES} match="any">
                    <InstallersPage />
                  </RequireRoles>
                }
              />
            ))}
            {/* The supplier id is part of the URL so a link to one supplier keeps
          working — the Angular screen did the same and people bookmark them. */}
            {['/production/suppliers', '/production/suppliers/:supplierId'].map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <RequireRoles roles={['internal-user', 'auditor']} match="any">
                    <SuppliersPage />
                  </RequireRoles>
                }
              />
            ))}
            <Route
              path="/production/dashboard"
              element={
                <RequireRoles roles={PRODUCTION_DASHBOARD_ROLES}>
                  <ProductionDashboardPage />
                </RequireRoles>
              }
            />
            <Route
              path="/production/planner"
              element={
                <RequireRoles roles={['admin', 'production-admin', 'auditor']} match="any">
                  <ProductionPlannerPage />
                </RequireRoles>
              }
            />
            <Route
              path="/production/report"
              element={
                <RequireRoles roles={['admin', 'production-admin', 'auditor']} match="any">
                  <DateChangesPage />
                </RequireRoles>
              }
            />
            <Route
              path="/production/orders/:tab?"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <OrdersPage />
                </RequireRoles>
              }
            />
            <Route
              path="/production/reports/:report?"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <ProductionReportsPage />
                </RequireRoles>
              }
            />
            <Route
              path="/production/stock-control/:view?"
              element={
                <RequireRoles roles={['admin', 'production-admin', 'auditor']} match="any">
                  <StockControlPage />
                </RequireRoles>
              }
            />
            <Route
              path="/contracts/reports/:report?"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <ContractsReportsPage />
                </RequireRoles>
              }
            />
            <Route
              path="/contracts/drawings"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <DrawingBookingsPage />
                </RequireRoles>
              }
            />
            <Route
              path="/utils/gp-calculator"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <GpCalculatorPage />
                </RequireRoles>
              }
            />
            <Route
              path="/reports/project-savings/:tab?"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <ProjectSavingsPage />
                </RequireRoles>
              }
            />
            <Route
              path="/contracts/planner"
              element={
                <RequireRoles roles={['internal-user', 'auditor']} match="any">
                  <PlannerPage />
                </RequireRoles>
              }
            />
            <Route
              path="/company-settings"
              element={
                <RequireAuth>
                  <CompanySettingsPage />
                </RequireAuth>
              }
            />
            <Route
              path="/user-settings"
              element={
                <RequireAuth>
                  <UserSettingsPage />
                </RequireAuth>
              }
            />
            {/* Every signed-in account has an inbox — a distributor gets the
          announcements aimed at them even though none of the role-gated screens
          above are theirs — so this is RequireAuth and not RequireRoles. */}
            <Route
              path="/notifications"
              element={
                <RequireAuth>
                  <NotificationsPage />
                </RequireAuth>
              }
            />
            {/* Support is the one pair of routes here that anyone can reach. The
          menu bar offers it beside the Login button, and somebody who cannot
          sign in is the likeliest person to need it, so no auth wrapper. */}
            <Route path="/support" element={<SupportPage />} />
            <Route path="/support/complete" element={<SupportCompletePage />} />
            <Route
              path="/downloads"
              element={
                <RequireRoles roles={['internal-user']}>
                  <DownloadsPage />
                </RequireRoles>
              }
            />
            {/* Where Auth0 returns after a sign-in. Only reached when this app
          is served on its own — inside the Angular host, `/callback` is the
          host's own route and the remote never sees it. */}
            <Route path="/callback" element={<AuthCallbackPage />} />
            <Route path="/terms-of-use" element={<TermsOfUsePage />} />
            {/* Release notes. Everyone who signs in gets the same ones — a
          distributor's screens change with a release as much as anyone's — so
          this is RequireAuth rather than RequireRoles. */}
            <Route
              path="/changelog"
              element={
                <RequireAuth>
                  <ChangelogPage />
                </RequireAuth>
              }
            />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            {/* Last: anything the routes above did not claim. */}
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </PageErrorBoundary>
    </AppShell>
  );
}

export default App;
